import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import SubHeader from '../../layout/components/SubHeader';
import dashboardService from '../../../services/dashboard.service';
import clinicService from '../../../services/clinic.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import DashboardAppointments from './subparts/DashboardAppointments';
import DashboardPatientInformation from './subparts/DashboardPatientInformation';
import DashboardPatientStats from './subparts/DashboardPatientStats';
import DashboardReturnOnInvestment from './subparts/DashboardReturnOnInvestment';
import DashboardAppointmentsRatio from './subparts/DashboardAppointmentsRatio';
import DashboardEmailStats from './subparts/DashboardEmailStats';
import DashboardSystemEmailStats from './subparts/DashboardSystemEmailStats';
import DashboardSmsStats from './subparts/DashboardSmsStats';
import DashboardCampaignStats from './subparts/DashboardCampaignStats';
import DashboardSectionNav from './subparts/DashboardSectionNav';
import { buildStatsMap, getLatestStatTimestamp } from './dashboardStatsConfig';
import { resolveStatValue } from './dashboardStatUtils';
import './Dashboard.css';

const ECP_FILTER_ROLES = ['MANAGER', 'STAFF', 'ECP'];

const getEcpDisplayName = (ecp) => {
    const name = [ecp?.firstName, ecp?.lastName].filter(Boolean).join(' ').trim();
    return name || ecp?.username || 'ECP';
};

function Dashboard() {
    const user = useEyeconxStore((state) => state.user);
    const userInfo = useEyeconxStore((state) => state.userInfo);
    const setTodaysAppointment = useEyeconxStore((state) => state.setTodaysAppointment);
    const clinicFeatures = useEyeconxStore((state) => state.clinicFeatures);
    const clinicFeaturesLoaded = useEyeconxStore((state) => state.clinicFeaturesLoaded);
    const appointmentsEnabled = clinicFeaturesLoaded && clinicFeatures?.isAppointments === true;

    const [dateFilter, setDateFilter] = useState('today');
    const [selectedEcpId, setSelectedEcpId] = useState('');
    const [ecpList, setEcpList] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [statsMap, setStatsMap] = useState({});
    const [statsRecords, setStatsRecords] = useState([]);
    const [isLoadingAppointments, setIsLoadingAppointments] = useState(true);
    const [isLoadingStats, setIsLoadingStats] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [appointmentError, setAppointmentError] = useState('');
    const [statsError, setStatsError] = useState('');
    const [activeTab, setActiveTab] = useState('patients');

    const canFilterByEcp = ECP_FILTER_ROLES.includes(user?.role);
    const getStat = useCallback((key) => resolveStatValue(statsMap, key), [statsMap]);

    const clinicLabel = userInfo?.firstName
        ? `${userInfo.firstName}${userInfo.lastName ? ` ${userInfo.lastName}` : ''}`
        : 'Clinic overview';

    const confirmedCount = useMemo(
        () => appointments.filter((appt) => appt.confirmed).length,
        [appointments]
    );

    const lastUpdated = useMemo(() => getLatestStatTimestamp(statsRecords), [statsRecords]);

    const tabs = useMemo(() => {
        const items = [
            { id: 'patients', title: 'Patients', icon: 'bi-people-fill' },
            { id: 'roi', title: 'ROI', icon: 'bi-graph-up-arrow' },
        ];

        if (appointmentsEnabled) {
            items.push({ id: 'appointments-ratio', title: 'Appointments', icon: 'bi-graph-up' });
        }

        items.push(
            { id: 'email', title: 'Email', icon: 'bi-envelope-paper' },
            { id: 'sms', title: 'SMS', icon: 'bi-chat-dots' },
            { id: 'campaigns', title: 'Campaigns', icon: 'bi-megaphone' }
        );

        return items;
    }, [appointmentsEnabled]);

    useEffect(() => {
        if (!tabs.some((tab) => tab.id === activeTab)) {
            setActiveTab(tabs[0]?.id || 'patients');
        }
    }, [activeTab, tabs]);

    const description = useMemo(() => {
        if (isLoadingAppointments) {
            return clinicLabel;
        }
        if (!appointments.length) {
            return `${clinicLabel} · No appointments ${dateFilter}`;
        }
        return `${clinicLabel} · ${appointments.length} appointment${appointments.length === 1 ? '' : 's'} · ${confirmedCount} confirmed`;
    }, [appointments.length, clinicLabel, confirmedCount, dateFilter, isLoadingAppointments]);

    useEffect(() => {
        if (!canFilterByEcp || !user?.clinicId) {
            setEcpList([]);
            return;
        }

        clinicService
            .getUserByRole(user.clinicId, 'ecp')
            .then((res) => setEcpList(Array.isArray(res.data) ? res.data : []))
            .catch(() => setEcpList([]));
    }, [canFilterByEcp, user?.clinicId]);

    const loadAppointments = useCallback(
        async ({ silent = false } = {}) => {
            if (!user?.clinicId || !appointmentsEnabled) {
                setAppointments([]);
                setIsLoadingAppointments(false);
                if (dateFilter === 'today') {
                    setTodaysAppointment([]);
                }
                return;
            }

            if (!silent) {
                setIsLoadingAppointments(true);
            }
            setAppointmentError('');

            try {
                const response = await dashboardService.getAppointment(
                    user.clinicId,
                    dateFilter,
                    selectedEcpId || undefined
                );
                const list = Array.isArray(response.data) ? response.data : [];
                setAppointments(list);

                if (dateFilter === 'today') {
                    setTodaysAppointment(list);
                }
            } catch (error) {
                console.error('Failed to load dashboard appointments', error);
                setAppointments([]);
                setAppointmentError('Unable to load appointments. Please try again.');
                if (dateFilter === 'today') {
                    setTodaysAppointment([]);
                }
            } finally {
                setIsLoadingAppointments(false);
            }
        },
        [appointmentsEnabled, dateFilter, selectedEcpId, setTodaysAppointment, user?.clinicId]
    );

    const loadStats = useCallback(
        async ({ silent = false } = {}) => {
            if (!user?.clinicId) {
                return;
            }

            if (!silent) {
                setIsLoadingStats(true);
            }
            setStatsError('');

            try {
                const response = await dashboardService.getDashboardStats(user.clinicId);
                const records = Array.isArray(response.data) ? response.data : [];
                setStatsRecords(records);
                setStatsMap(buildStatsMap(records));
            } catch (error) {
                console.error('Failed to load dashboard stats', error);
                setStatsRecords([]);
                setStatsMap({});
                setStatsError('Patient stats are not available yet. They refresh on a scheduled job.');
            } finally {
                setIsLoadingStats(false);
            }
        },
        [user?.clinicId]
    );

    useEffect(() => {
        loadAppointments();
    }, [loadAppointments]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await Promise.all([loadAppointments({ silent: true }), loadStats({ silent: true })]);
            toast.success('Dashboard refreshed');
        } finally {
            setIsRefreshing(false);
        }
    };

    const sharedStatsProps = {
        getStat,
        isLoading: isLoadingStats,
        loadError: statsError,
        lastUpdated,
    };

    const renderActivePanel = () => {
        switch (activeTab) {
            case 'roi':
                return <DashboardReturnOnInvestment {...sharedStatsProps} />;
            case 'appointments-ratio':
                return appointmentsEnabled ? <DashboardAppointmentsRatio {...sharedStatsProps} /> : null;
            case 'email':
                return (
                    <div className="dashboard-email-stack">
                        <DashboardEmailStats {...sharedStatsProps} />
                        <DashboardSystemEmailStats {...sharedStatsProps} />
                    </div>
                );
            case 'sms':
                return <DashboardSmsStats {...sharedStatsProps} />;
            case 'campaigns':
                return <DashboardCampaignStats {...sharedStatsProps} />;
            case 'patients':
            default:
                return (
                    <div className="dashboard-patient-stack">
                        <DashboardPatientStats {...sharedStatsProps} />
                        <DashboardPatientInformation {...sharedStatsProps} />
                    </div>
                );
        }
    };

    return (
        <div className="ecx-page dashboard-page ecx-page--full-width">
            <SubHeader
                title="Dashboard"
                description={description}
                icon="bi-speedometer2"
                containerClass="w-100 px-4"
            >
                <button
                    type="button"
                    className="btn btn-light-primary btn-sm rounded-pill"
                    onClick={handleRefresh}
                    disabled={isLoadingAppointments || isLoadingStats || isRefreshing}
                >
                    <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'ecx-refresh-spin' : ''}`} />
                    Refresh
                </button>
            </SubHeader>

            <div className="ecx-page-body">
                <div className="row g-3 dashboard-layout-grid">
                    <div className={`col-12 ${appointmentsEnabled ? 'col-lg-8' : 'col-lg-12'} order-2 order-lg-1`}>
                        <div className="dashboard-main-stack">
                            <DashboardSectionNav
                                sections={tabs}
                                activeSection={activeTab}
                                onSectionChange={setActiveTab}
                            />
                            <div
                                id={`dashboard-panel-${activeTab}`}
                                role="tabpanel"
                                aria-labelledby={`dashboard-tab-${activeTab}`}
                                className="dashboard-tab-panel"
                            >
                                {renderActivePanel()}
                            </div>
                        </div>
                    </div>

                    {appointmentsEnabled && (
                        <div className="col-12 col-lg-4 order-1 order-lg-2">
                            <DashboardAppointments
                                dateFilter={dateFilter}
                                onDateFilterChange={setDateFilter}
                                selectedEcpId={selectedEcpId}
                                onEcpChange={setSelectedEcpId}
                                ecpList={ecpList}
                                canFilterByEcp={canFilterByEcp}
                                getEcpDisplayName={getEcpDisplayName}
                                appointments={appointments}
                                isLoading={isLoadingAppointments}
                                loadError={appointmentError}
                                confirmedCount={confirmedCount}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
